import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Segment, Radio } from 'tea-component';
import { useAiSummary, useLocal } from '@/pages/dashboard/utils/customHook';
import { getAlignmentAiDataKeyField } from '@/pages/dashboard/utils/aiAlignmentUtils';
import { getName } from '@home/utils/common';
import classNames from 'classnames';
import SingleSelect from '@home/components/SingleSelect';
import { dynamicString } from '@home/utils/dynamicString';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';
import { ImageDownloadButton } from '@/pages/dashboard/components/Plugin/ImageDownloadButton';
import TipContext from '@home/components/TipContext';
import '@/pages/dashboard/components/Plugin/index.scss';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { getCountryFlag } from '@home/_config/countries';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import Abnormal from '@home/components/Abnormal';
import './index.scss';
import { extendChartAxesRange } from '@/pages/dashboard/components/Chart/_config/plug';
import CommonTable from '@/pages/dashboard/components/CommonTable';
import type { IColumn } from '@/pages/dashboard/components/CommonTable/type';
import { convertSortFormat } from '@/pages/dashboard/components/CommonTable/utils';
import Loading from '@/pages/dashboard/components/Loading';
import { iconMap } from './constant';
import {
  injectCompactTableStyle,
  removeCompactTableStyle,
  applyExportProgressBarInlineStyles,
  inlineSvgUseElements,
} from './imageExportStyle';
import ViewportFullscreenTip from '@/pages/dashboard/components/ViewportFullscreenTip';

/** 全屏时表格高度 = 视口高度 − 该值（顶栏、摘要、全屏 padding 等）；调大可避免摘要过长时裁切，调小则表格更高 */
const FULLSCREEN_TABLE_CHROME_RESERVE = 260;

/**
 * Service 已通过 changeData 维护 `data.extraParams`（page_size、metrics / sorted_by）。
 * 视口全屏时子树在 inline 与 Portal 间切换会卸载重挂，若仅用 tabOption/selectConfig 的 defaultValue，
 * 会与当前请求参数不一致（如已选 Top 10 却回到 Top 30）。
 */
function deriveSegmentValueFromExtraParams(
  pageData: { extraParams?: { page_size?: unknown; }; } | undefined,
  tabOption: {
    defaultValue?: string | number;
    tabOptions?: Array<{ value?: string | number; }>;
  } | undefined,
): string | number | undefined {
  const ps = pageData?.extraParams?.page_size;
  if (ps === undefined || ps === null || ps === '') {
    return tabOption?.defaultValue;
  }
  const sample = tabOption?.tabOptions?.[0]?.value;
  if (typeof sample === 'number') {
    const n = Number(ps);
    return Number.isNaN(n) ? tabOption?.defaultValue : n;
  }
  return String(ps);
}

function deriveSelectValueFromExtraParams(
  pageData: {
    extraParams?: { metrics?: unknown; sorted_by?: unknown; };
  } | undefined,
  useMetricsParam: boolean,
  selectConfig: { defaultValue?: string; } | undefined,
): string {
  const ep = pageData?.extraParams;
  if (!ep) {
    return selectConfig?.defaultValue as string;
  }
  if (useMetricsParam) {
    const m = ep.metrics;
    if (Array.isArray(m) && m.length > 0 && m[0] !== undefined && m[0] !== null) {
      return String(m[0]);
    }
  } else if (ep.sorted_by !== undefined && ep.sorted_by !== null && ep.sorted_by !== '') {
    return String(ep.sorted_by);
  }
  return selectConfig?.defaultValue as string;
}

function getAlignmentAiDataKey(
  req: { data_keys?: Record<string, string>; data_key?: string; } | undefined,
  alignmentValue: string,
  enabled: boolean,
  alignmentDataKeyMap?: Record<string, string | undefined>,
): string | undefined {
  if (!enabled) {
    return req?.data_key;
  }
  const dataKeyField = getAlignmentAiDataKeyField(
    alignmentValue,
    alignmentDataKeyMap,
  );
  return req?.data_keys?.[dataKeyField] || req?.data_key;
}

function getAlignmentAiSummary(
  req: {
    data_keys?: Record<string, string>;
    ai_analysis?: Record<string, string>;
    data_analysis?: string;
  } | undefined,
  alignmentValue: string,
  enabled: boolean,
  alignmentDataKeyMap?: Record<string, string | undefined>,
): string | undefined {
  const dataKeyField = getAlignmentAiDataKeyField(
    alignmentValue,
    alignmentDataKeyMap,
  );
  if (!enabled || !req?.data_keys?.[dataKeyField]) {
    return req?.data_analysis;
  }
  return req?.ai_analysis?.[dataKeyField];
}

function resolveTopInfoForSelect(
  selectVal: string,
  selectConfig: { selectOptions?: Array<{ value?: string; topInfo?: unknown; }>; } | undefined,
): any {
  const opts = selectConfig?.selectOptions || [];
  const hit = opts.find(item => String(item.value) === String(selectVal));
  return hit?.topInfo || opts[0]?.topInfo || {};
}

type TAlignmentColumnHeader = {
  nameEn?: string;
  nameCn?: string;
};

type TTableColumnHeaderSwitchItem = {
  code: string;
  version_aligned?: TAlignmentColumnHeader;
  natural_day_aligned?: TAlignmentColumnHeader;
};

type TTableColumnHeaderSwitchConfig = {
  columns?: TTableColumnHeaderSwitchItem[];
};

type TAlignmentOverride = Partial<{
  selectConfig: Record<string, unknown>;
  metricsColumnMap: Record<string, IColumn[]>;
  aiTagMap: Record<string, string>;
  metricUrlMap: Record<string, string>;
}>;

/** 按对齐方式覆盖固定表格列的表头（仅影响表格展示与下载，不重请求） */
function applyTableColumnHeaderSwitch(
  columns: IColumn[] | undefined,
  switchConfig: TTableColumnHeaderSwitchConfig | undefined,
  alignmentValue: string,
): IColumn[] | undefined {
  if (!switchConfig?.columns?.length || !Array.isArray(columns)) {
    return columns;
  }
  return columns.map(col => {
    const item = switchConfig.columns?.find(
      entry => entry.code === col.code,
    );
    if (!item) {
      return col;
    }
    const override =
      item[alignmentValue as 'version_aligned' | 'natural_day_aligned'];
    if (!override) {
      return col;
    }
    return {
      ...col,
      nameEn: override.nameEn ?? col.nameEn,
      nameCn: override.nameCn ?? col.nameCn,
    };
  });
}

/**
 * alignmentConfig 形如 ChangeAnalysisTrendCard：
 * { version_aligned: { selectConfig, metricsColumnMap, ... }, natural_day_aligned: { ... } }
 * 命中 alignmentValue 后浅合并覆盖顶层 props；切换对齐不重新请求宽表接口。
 */
const resolveAlignmentProps = (
  alignmentValue: string,
  alignmentConfig: Record<string, TAlignmentOverride> | undefined,
  fallback: TAlignmentOverride,
): TAlignmentOverride => {
  const override = alignmentConfig?.[alignmentValue] ?? {};
  return {
    selectConfig: override.selectConfig ?? fallback.selectConfig,
    metricsColumnMap: override.metricsColumnMap ?? fallback.metricsColumnMap,
    aiTagMap: override.aiTagMap ?? fallback.aiTagMap,
    metricUrlMap: override.metricUrlMap ?? fallback.metricUrlMap,
  };
};

type TClientTableSortBackend = Array<{ sort_by: string; desc: boolean; }>;

/** 表格行数据：字段名 → 任意取值（含运行期注入的 `__col_range_pct_*` 归一化宽度字段） */
type TTableRow = Record<string, unknown>;

/** 进度条配置：在通用 progressBarProps 基础上扩展"按列范围归一化"所需字段 */
interface IColumnRangeBarConfig {
  mode?: 'left' | 'center';
  /** 为 true 时按列正/负基准归一化宽度，原始 value 不再乘 100 */
  basedOnColumnRange?: boolean;
  /** record 中存放已归一化宽度（0~100）的字段名 */
  widthKey?: string;
  [key: string]: unknown;
}

/** 单元格配置（仅声明本逻辑需要读写的字段） */
interface IColumnRangeCell {
  type?: string;
  progressBarProps?: IColumnRangeBarConfig;
  [key: string]: unknown;
}

/** 列的正/负基准 */
interface IColumnRangeBase {
  posBase: number;
  negBase: number;
}

const ChangeAnalysisCountryRegionChart = (props: any) => {
  const {
    titleCn,
    titleEn,
    children,
    tabOption,
    className,
    style,
    selectConfig,
    subTitle,
    subTitleCn,
    data,
    changeData,
    barLineChartConfig,
    doubleBarChartConfig,
    apiDataKey = 'trendData',
    tableConfig,
    aiTag='',
    // ===== 扩展功能 props =====
    tableOnly = false,       // 纯表格模式，隐藏图表/表格切换
    aiTagMap,                // 指标 → AI Tag 映射，支持按下拉选项动态切换 aiTag
    metricsColumnMap,        // 指标 → 表格列配置映射，支持按下拉选项动态切换列
    useMetricsParam = false, // 接口参数使用 metrics 数组替代 sorted_by
    tableCountryJumpLink,    // 表格国家列点击跳转 URL 模板
    metricUrlMap,            // selectValue → 跳转 URL metric 参数映射，解决 key 不一致问题
    enableImageDownload = false, // 是否在右上角展示「图片下载」入口（PNG 完整高度截图）
    /**
     * 导出图片时表格行高（CSS px）。
     * 不配置则不对导出图片做行间距压缩；配置后同时影响 --row-height 变量、表头 th 和表格行 td 的上下 padding。
     * padding 计算方式：(imageExportRowHeight - 内容行高约24px) / 2，最小 2px。
     */
    imageExportRowHeight,
    /**
     * 导出图片时表头字体大小（CSS px），默认 15。
     */
    imageExportHeaderFontSize,
    /**
     * 导出图片时表内容字体大小（CSS px），默认 15。
     */
    imageExportContentFontSize,
    /**
     * 导出图片时进度条色块高度（CSS px，相对整行高度 imageExportRowHeight）。
     * 数值越大色块越高，最大不超过行高；例如行高 20 配 18 则上下各留 1px。
     * 不配置则按内容区高度 44% 自动计算。
     */
    imageExportProgressBarHeight,
    /**
     * 导出图片时表格整体宽度（CSS 像素值字符串，如 '1600px'）。
     * 截图前临时将表格容器撑到该宽度，各列按原始比例自动分配，
     * 截图出来就是宽横向比例、可直接贴入 PPT 而无需拉伸失真。
     * 不配置则按页面实际宽度截图。
     */
    imageExportTableWidth,
    /**
     * 是否按"指标-Tab"动态拼装下载/导出文件名（例如 `周平均活跃天数-Top30`）。
     * 默认 false，保持原有的表格标题命名以避免回归。
     * - 开启图片导出时通常希望文件名能反映当前筛选条件 → 建议同时开启此项；
     * - 也可单独在其他实例上开启，用于纯 Excel 下载场景。
     */
    useMetricTabFileName = false,
    /** 版本周对齐 / 自然日对齐：按 alignment 覆盖 selectConfig、metricsColumnMap 等（不触发宽表重请求） */
    alignmentConfig,
    /** 指标下拉右侧的对齐切换（仅 UI，本地 state，不重请求） */
    alignmentSwitchConfig,
    /** 指定指标下隐藏对齐切换（仍按 defaultValue 对齐渲染列） */
    alignmentSwitchHiddenMetrics = [],
    enableAlignmentAiDataKey = false,
    alignmentDataKeyMap,
    /** 固定 columns 场景：按对齐覆盖指定列的表头（仅表格 + 下载） */
    tableColumnHeaderSwitch,
    /** 由外层 ViewportFullscreenShell 注入（pageConfig 包裹 Shell 时） */
    viewportFullscreen,
    ...restProps
  } = props;
  const { isEN, formatMessage } = useLocal();
  const pageData = data;
  const defaultAlignmentValue =
    alignmentSwitchConfig?.defaultValue ?? 'version_aligned';
  const [alignmentValue, setAlignmentValue] = useState<string>(
    defaultAlignmentValue,
  );
  const [selectValue, setSelectValue] = useState(() =>
    deriveSelectValueFromExtraParams(pageData, useMetricsParam, selectConfig),
  );
  const isAlignmentSwitchHidden = useMemo(
    () =>
      alignmentSwitchHiddenMetrics.some(
        (metric: string) => String(metric) === String(selectValue),
      ),
    [alignmentSwitchHiddenMetrics, selectValue],
  );
  const effectiveAlignmentValue = isAlignmentSwitchHidden
    ? defaultAlignmentValue
    : alignmentValue;
  const resolvedAlignmentProps = useMemo(
    () =>
      resolveAlignmentProps(effectiveAlignmentValue, alignmentConfig, {
        selectConfig,
        metricsColumnMap,
        aiTagMap,
        metricUrlMap,
      }),
    [
      effectiveAlignmentValue,
      alignmentConfig,
      selectConfig,
      metricsColumnMap,
      aiTagMap,
      metricUrlMap,
    ],
  );
  const effectiveSelectConfig = resolvedAlignmentProps.selectConfig as typeof selectConfig;
  const effectiveMetricsColumnMap = resolvedAlignmentProps.metricsColumnMap;
  const effectiveAiTagMap = resolvedAlignmentProps.aiTagMap;
  const effectiveMetricUrlMap = resolvedAlignmentProps.metricUrlMap;
  const [topInfo, setTopInfo] = useState<any>(() =>
    resolveTopInfoForSelect(
      deriveSelectValueFromExtraParams(pageData, useMetricsParam, selectConfig),
      selectConfig,
    ),
  );
  const [segmentValue, setSegmentValue] = useState<string | number | undefined>(() =>
    deriveSegmentValueFromExtraParams(pageData, tabOption),
  );
  const [activeType, setActiveType] = useState('column');
  // 标记用户是否刚切换了下拉/Tab，用于 AI 文案区域立即显示 loading
  const [isParamsSwitching, setIsParamsSwitching] = useState<boolean>(false);
  const [isAlignmentAiSwitching, setIsAlignmentAiSwitching] = useState<boolean>(false);
  const chartRef = useRef<any>(null);
  const tableRef = useRef<any>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const tableExportRef = useRef<HTMLDivElement>(null);
  const prevServiceLoadingRef = useRef<boolean>(false);
  // 存储表格排序后的数据，用于下载
  const [sortedTableData, setSortedTableData] = useState<any[]>([]);
  // 标记是否正在重置 segmentValue，防止用旧值渲染和发起请求
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const isInitialParamsSync = useRef(true);
  /** Portal 换容器时子树可能重挂；用 Shell 内 ref 备份列排序（非 sessionStorage / Context） */
  const tableSortSnapId = useMemo(() => {
    const mapKeys =
      effectiveAiTagMap && typeof effectiveAiTagMap === 'object'
        ? Object.keys(effectiveAiTagMap).sort().join(',')
        : '';
    return [
      'cacrc',
      String((pageData as { gameCode?: string; })?.gameCode ?? ''),
      effectiveAlignmentValue,
      tableOnly ? 'to' : 'ch',
      aiTag || (mapKeys ? `map:${mapKeys}` : 'notag'),
      String(effectiveSelectConfig?.defaultValue ?? ''),
      String(tabOption?.defaultValue ?? ''),
      className || '',
    ].join(':');
  }, [
    pageData,
    effectiveAlignmentValue,
    tableOnly,
    aiTag,
    effectiveAiTagMap,
    effectiveSelectConfig?.defaultValue,
    tabOption?.defaultValue,
    className,
  ]);
  const [clientTableSort, setClientTableSort] = useState<
  TClientTableSortBackend | undefined
  >(undefined);
  const purgeClientTableSort = useCallback(() => {
    setClientTableSort(undefined);
    if (viewportFullscreen?.enabled) {
      viewportFullscreen.setChildSortSnapshot?.(tableSortSnapId, undefined);
    }
  }, [
    tableSortSnapId,
    viewportFullscreen?.enabled,
    viewportFullscreen?.setChildSortSnapshot,
  ]);
  /** 仅在实际切换指标 / TopN / 对齐 / snap 维度时清空，避免挂载或 Portal 重挂后误清 Shell 内排序 */
  const sortFilterPrevRef = useRef<{
    selectValue: string;
    segmentValue: string | number | undefined;
    alignmentValue: string;
    effectiveAlignmentValue: string;
    tableSortSnapId: string;
  } | null>(null);
  useEffect(() => {
    const prev = sortFilterPrevRef.current;
    if (
      prev &&
      prev.selectValue === selectValue &&
      String(prev.segmentValue ?? '') === String(segmentValue ?? '') &&
      prev.alignmentValue === alignmentValue &&
      prev.effectiveAlignmentValue === effectiveAlignmentValue &&
      prev.tableSortSnapId === tableSortSnapId
    ) {
      return;
    }
    sortFilterPrevRef.current = {
      selectValue,
      segmentValue,
      alignmentValue,
      effectiveAlignmentValue,
      tableSortSnapId,
    };
    if (prev) {
      purgeClientTableSort();
    }
  }, [
    selectValue,
    segmentValue,
    alignmentValue,
    effectiveAlignmentValue,
    tableSortSnapId,
    purgeClientTableSort,
  ]);

  /** 对齐切换：校验下拉值是否仍有效（不触发宽表接口重请求） */
  const prevAlignmentRef = useRef(alignmentValue);
  useEffect(() => {
    if (prevAlignmentRef.current === alignmentValue) {
      return;
    }
    prevAlignmentRef.current = alignmentValue;
    const opts = effectiveSelectConfig?.selectOptions || [];
    const validValues = opts.map((item: { value?: string; }) =>
      String(item.value),
    );
    if (!validValues.includes(String(selectValue))) {
      const nextValue = (effectiveSelectConfig?.defaultValue ?? '') as string;
      setSelectValue(nextValue);
      setTopInfo(resolveTopInfoForSelect(nextValue, effectiveSelectConfig));
      return;
    }
    setTopInfo(resolveTopInfoForSelect(selectValue, effectiveSelectConfig));
  }, [alignmentValue, effectiveSelectConfig, selectValue]);
  useLayoutEffect(() => {
    if (!viewportFullscreen?.enabled || !viewportFullscreen.getChildSortSnapshot) {
      return;
    }
    const snap = viewportFullscreen.getChildSortSnapshot(
      tableSortSnapId,
    ) as TClientTableSortBackend | undefined;
    if (Array.isArray(snap) && snap.length > 0) {
      setClientTableSort(snap);
    }
  }, [
    viewportFullscreen?.enabled,
    viewportFullscreen?.isFullScreen,
    tableSortSnapId,
  ]);
  const commonTableDataForSort = useMemo(() => {
    const base = pageData || {};
    if (clientTableSort === undefined) {
      return base;
    }
    return { ...base, clientTableSort };
  }, [pageData, clientTableSort]);
  const { barMetricConfig, lineMetricConfig } = barLineChartConfig || {};
  const { firstBarConfig, secondBarConfig } = doubleBarChartConfig || {};
  const { data: chartData, errorMsg, loading, req } = data?.[apiDataKey];
  // const aiSummary = req?.data_analysis;
  const currentAiDataKey = getAlignmentAiDataKey(
    req,
    effectiveAlignmentValue,
    enableAlignmentAiDataKey,
    alignmentDataKeyMap,
  );
  const inlineAiSummary = getAlignmentAiSummary(
    req,
    effectiveAlignmentValue,
    enableAlignmentAiDataKey,
    alignmentDataKeyMap,
  );
  // 文件名 fallback：当选项尚未派生出 text 时回落到表格标题
  const downLoadFileTitleFallback = getName(isEN, tableConfig?.titleCn, tableConfig?.titleEn);

  const {
    xData,
    xDataCn,
    lineData,
    barData,
    countryCode,
    firstCompareData,
    secondCompareData,
    tableData,
    rawData,
  } = chartData || {};
  // x轴数据中英文
  const xAxisData = getName(isEN, xData, xDataCn);
  const barLineNodata = !lineData?.length && !barData?.length;
  const compareBarNodata = !firstCompareData?.length && !secondCompareData?.length;
  // 新数据到达时（data_key 变化）清除参数切换标记
  // 确保 tag 和 data_key 一定是匹配的，防止用错误参数调 AI 接口污染后端缓存
  useEffect(() => {
    setIsParamsSwitching(false);
  }, [req?.data_key]);
  useEffect(() => {
    if (prevServiceLoadingRef.current && !loading) {
      setIsParamsSwitching(false);
    }
    prevServiceLoadingRef.current = Boolean(loading);
  }, [loading]);

  // 动态 AI Tag：如果配置了 aiTagMap，根据当前下拉选项映射；否则使用静态 aiTag
  const currentAiTag = effectiveAiTagMap
    ? (effectiveAiTagMap[selectValue] || '')
    : aiTag;
  // 计算是否需要调用 AI 接口
  // isParamsSwitching 阻止切换期间用新 tag + 旧 data_key 发请求（防后端缓存污染）
  // 只有 data_key 更新后（新数据到达）才允许调用，此时 tag 和 data_key 一定匹配
  const shouldFetchAi = tableOnly
    ? (!loading && !isParamsSwitching && currentAiTag && !inlineAiSummary && tableData?.length > 0)
    : (!loading && !isParamsSwitching && currentAiTag && !inlineAiSummary && !barLineNodata && !compareBarNodata);

  const {
    data: aiData,
    loading: aiLoading,
  } = useAiSummary({
    game_code: data?.gameCode,
    data_key: currentAiDataKey,
    tag: currentAiTag,
    enabled: shouldFetchAi,
  });
  useEffect(() => {
    if (!aiLoading && !shouldFetchAi) {
      setIsAlignmentAiSwitching(false);
    }
  }, [aiLoading, shouldFetchAi]);
  useEffect(() => {
    if (!aiLoading) {
      setIsAlignmentAiSwitching(false);
    }
  }, [aiLoading]);
  // tab切换 - 根据 activeType 动态添加额外选项
  const tabOptions = useMemo(() => {
    if (!tabOption?.tabOptions) return [];

    let options = tabOption.tabOptions;

    // 当处于表格视图(sheet)或纯表格模式(tableOnly)时，添加额外选项（如 "All"）
    if ((activeType === 'sheet' || tableOnly) && tabOption?.sheetExtraOptions?.length > 0) {
      options = [...options, ...tabOption.sheetExtraOptions];
    }

    return options.map((item: any) => ({
      value: item.value,
      text: getName(isEN, item.enName, item.cnName),
    }));
  }, [tabOption?.tabOptions, tabOption?.sheetExtraOptions, isEN, activeType, tableOnly]);
  // 下拉选项（随 alignment 切换文案，metrics 请求参数不变）
  const selectOptions = useMemo(() => {
    if (!effectiveSelectConfig?.selectOptions) return [];
    return effectiveSelectConfig.selectOptions.map((item: any) => ({
      value: item.value,
      text: getName(isEN, item.enName, item.cnName),
      topInfo: item.topInfo
    }));
  }, [effectiveSelectConfig?.selectOptions, isEN]);

  const alignmentOptions = useMemo(() => {
    if (!alignmentSwitchConfig?.options?.length) {
      return [];
    }
    return alignmentSwitchConfig.options.map((item: {
      value: string;
      name?: string;
      nameCn?: string;
    }) => ({
      value: item.value,
      label: getName(isEN, item.name, item.nameCn),
    }));
  }, [alignmentSwitchConfig?.options, isEN]);

  /**
   * 下载/导出文件名：
   * - 仅在 pageConfig 显式开启 useMetricTabFileName 的实例上启用「表格标题-指标-Tab」拼装，
   *   例如 `非简中重点国家表现-活跃留存-Top30`、`非简中重点国家表现-周平均活跃天数-All`。
   * - 其他实例（如 changeAnalysis 里 SortBy 趋势图等）保持原有表格标题命名，避免回归。
   * - Tab 文案里的空格会被去掉（Top 30 → Top30），更适合作为文件名。
   * - 表格标题缺失时，回落到「指标-Tab」；任意一段缺失则用兜底标题。
   */
  const downLoadFileTitle = useMemo(() => {
    if (!useMetricTabFileName) {
      return downLoadFileTitleFallback;
    }
    const metricText = selectOptions?.find((o: any) => o.value === selectValue)?.text;
    const tabText = tabOptions?.find((o: any) => o.value === segmentValue)?.text;
    if (metricText && tabText) {
      const compactTab = String(tabText).replace(/\s+/g, '');
      const segments = [downLoadFileTitleFallback, metricText, compactTab].filter(Boolean);
      return segments.join('-');
    }
    return downLoadFileTitleFallback;
  }, [useMetricTabFileName, selectOptions, selectValue, tabOptions, segmentValue, downLoadFileTitleFallback]);

  // 下拉&tab切换接口参数刷新
  useEffect(() => {
    if (isResetting) {
      return;
    }

    // 首次 mount 跳过：Service config 中已有正确的初始 extraParams，
    // 避免创建新对象引用触发 Service useEffect 的二次请求
    if (isInitialParamsSync.current) {
      isInitialParamsSync.current = false;
      return;
    }

    if (useMetricsParam) {
      changeData('extraParams', {
        metrics: [selectValue],
        page_size: segmentValue,
      });
    } else {
      changeData('extraParams', {
        sorted_by: selectValue,
        page_size: segmentValue,
      });
    }
  }, [selectValue, segmentValue, isResetting]);

  // 处理表格排序变化：本地 state + 写入 Shell ref，防 Portal 换容器重挂丢排序
  const handleTableSortChange = useCallback(
    (sortsPopover?: unknown) => {
      if (Array.isArray(sortsPopover)) {
        const backend = convertSortFormat(
          sortsPopover,
          'backend',
        ) as TClientTableSortBackend;
        const next = backend.length ? backend : undefined;
        setClientTableSort(next);
        if (viewportFullscreen?.enabled) {
          viewportFullscreen.setChildSortSnapshot?.(tableSortSnapId, next);
        }
      }
      setTimeout(() => {
        const currentData = tableRef.current?.dataSource;
        if (currentData?.length > 0) {
          setSortedTableData(currentData);
        }
      }, 50);
    },
    [
      tableSortSnapId,
      viewportFullscreen?.enabled,
      viewportFullscreen?.setChildSortSnapshot,
    ],
  );

  // 切换视图类型，同时处理 segmentValue 重置
  const handleActiveTypeChange = (newType: string) => {
    if (newType === activeType) {
      return;
    }
    // 从 sheet 切换到 column 时，如果当前值是 sheetExtraOptions 中的值，需要同步重置
    if (newType !== 'sheet' && tabOption?.sheetExtraOptions?.length > 0) {
      const sheetExtraValues = tabOption.sheetExtraOptions.map((item: any) => item.value);
      if (sheetExtraValues.includes(segmentValue)) {
        // 设置重置标记，触发 loading 状态
        setIsResetting(true);
        purgeClientTableSort();
        setSegmentValue(tabOption?.defaultValue);
        // 下一个 tick 清除标记
        setTimeout(() => {
          setIsResetting(false);
        }, 100);
      }
    }
    setActiveType(newType);
  };

  // 右侧功能列表
  const operationList = [
    {
      key: 'column',
      tipText: formatMessage({ id: 'common_bar_chart' }),
      type: { default: 'column-chart', hover: 'column-chart' },
    },
    {
      key: 'sheet',
      tipText: formatMessage({ id: 'common_table' }),
      type: { default: 'sheet', hover: 'sheet' },
    },
  ];

  // 给 echarts 实例 y 轴增加刻度延伸
  useEffect(() => {
    if (activeType !== 'column') return;
    if (!chartRef.current?.instance()) return;

    const chart = chartRef.current.instance();
    if (!chart) return;

    // 延迟一点确保初始化完毕
    const timer = setTimeout(() => {
      extendChartAxesRange({
        dom: chart.getDom(),
        axes: [{ type: 'yAxis', index: 0, extendCount: 2 }],
      });
    }, 0); // 先不延迟

    return () => clearTimeout(timer);
  }, [chartData, activeType, isEN]);

  // 处理图表数据
  const { barSeries, lineSeries, formattedTableData, dauBarSeries } =
    useMemo(() => {
      // DAU环比变化率 - 在数据项级别配置 label position
      const barSeriesData = barData?.map((value: number) => ({
        value,
        itemStyle: {
          color: value >= 0 ? '#69D19C' : '#F17C86',
        },
        label: {
          position: value >= 0 ? 'top' : 'bottom',
        },
      }));
      // 折线图
      const lineSeries = [
        {
          name: getName(
            isEN,
            lineMetricConfig?.enName,
            lineMetricConfig?.cnName,
          ),
          type: 'line',
          yAxisIndex: 1,
          data: lineData || [],
          symbol: 'circle',
          symbolSize: 6,
          animation: false,
          lineStyle: {
            color: '#6E7794',
          },
          label: {
            show: false,
          },
          ...lineMetricConfig?.seriesConfig,
        },
      ];
      // markPoint 逻辑
      let markPointData: any[] = [];

      if (rawData && topInfo?.sortKey && topInfo?.marks?.length > 0) {
        const { sortKey, marks } = topInfo || {};

        // 创建带索引的数据数组
        const dataWithIndex = rawData?.map((item: any, index: number) => ({
          originalIndex: index,
          sortValue: parseFloat(item?.[sortKey]) || 0,
          barValue: barData?.[index] || 0,
        }));

        // 根据 sortKey 降序排序
        const sortedData = [...dataWithIndex]?.sort((a, b) => b?.sortValue - a?.sortValue);

        // 动态生成 markPoint 数据（支持 top1, top2, top3, top4...）
        markPointData = marks?.map((mark: any, index: number) => {
          const topData = sortedData?.[index];
          if (!topData) return null; // 如果数据不足，跳过

          return {
            coord: [topData?.originalIndex, topData?.barValue],
            name: mark?.label,
            // 支持自定义颜色
            itemStyle: mark.color ? { color: mark.color } : undefined,
          };
        }).filter(Boolean);
      }

      // 柱状图
      const barSeries = [
        {
          name: getName(isEN, barMetricConfig?.enName, barMetricConfig?.cnName),
          type: 'bar',
          data: barSeriesData || [],
          animation: false,
          label: {
            show: true,
            fontSize: 12,
            formatter: (params: any) => {
              const value =
                params?.data?.value ?? params?.value ?? params?.data;
              return (
                dataFormat({
                  value,
                  formatRule: barMetricConfig?.formatRule,
                }) || ''
              );
            },
            color: '#262832',
            overflow: 'truncate',
            hideOverlap: true,
          },
          markPoint: {
            symbol:
              'path://M4,0 H36 A4,4 0 0 1 40,4 V16 A4,4 0 0 1 36,20 H4 A4,4 0 0 1 0,16 V4 A4,4 0 0 1 4,0 Z',
            symbolSize: topInfo?.symbolSize || [60, 20],
            symbolOffset: [0, -40],
            silent: true,
            label: {
              show: !!topInfo,
              fontSize: '10px',
              color: '#fff',
              offset: [0, 0.25],
              lineHeight: 12,
              formatter: (params: { name: string; }) => `${params.name}`,
            },
            data: markPointData,
            itemStyle: {
              color: 'rgba(38, 40, 50, 0.65)',
            },
          },
          ...barMetricConfig?.seriesConfig,
        },
      ];
      // 在 firstBarData 中为第一个和最后一个数据点设置 align
      const firstBarData = firstCompareData?.map((value: number, index: number) => {
        const dataItem: any = { value };

        // 第一个数据点的 align 配置
        if (index === 0 && firstBarConfig?.firstLabelOffset) {
          dataItem.label = {
            align: firstBarConfig.firstLabelOffset.align || 'left',
          };
        }
        // 最后一个数据点的 align 配置
        else if (index === (firstCompareData.length - 1) && firstBarConfig?.lastLabelOffset) {
          dataItem.label = {
            align: firstBarConfig.lastLabelOffset.align || 'right',
          };
        }

        return dataItem;
      });
      // 双柱状图
      const dauBarSeries = [
        {
          name: getName(isEN, firstBarConfig?.enName, firstBarConfig?.cnName),
          type: 'bar',
          data: firstBarData || [],
          barMaxWidth: 30,
          itemStyle: {
            color: '#5086F3',
          },
          animation: false,
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => {
              const value =
                params?.data?.value ?? params?.value ?? params?.data;
              return (
                dataFormat({
                  value,
                  formatRule: firstBarConfig?.formatRule,
                }) || ''
              );
            },
            color: '#262832',
            fontSize: 12,
            overflow: 'truncate',
            minMargin: 10,
          },
          labelLayout: (params: any) => {
            const dataLength = firstCompareData?.length || 0;
            const isFirst = params.dataIndex === 0;
            const isLast = params.dataIndex === dataLength - 1;

            const barWidth = params.rect?.width || 15; // 柱状图宽度的一半
            // 对于第一个和最后一个数据点，使用配置的 offset
            if (isFirst && firstBarConfig?.firstLabelOffset) {
              return {
                dx: -barWidth / 2, // 水平偏移
                hideOverlap: false, // 不隐藏重叠
              };
            }

            if (isLast && firstBarConfig?.lastLabelOffset) {
              return {
                dx: barWidth * 1.5, // 水平偏移
                hideOverlap: false, // 不隐藏重叠
              };
            }

            // 对于其他数据点，启用 hideOverlap 避免重叠
            return {
              hideOverlap: true,
            };
          },
          emphasis: {
            disabled: true,
          },
          ...firstBarConfig?.seriesConfig,
        },
        {
          name: getName(isEN, secondBarConfig?.enName, secondBarConfig?.cnName),
          type: 'bar',
          data: secondCompareData || [],
          barMaxWidth: 30,
          itemStyle: {
            color: '#69D19C',
          },
          animation: false,
          label: {
            show: false,
            formatter: (params: any) => {
              const value =
                params?.data?.value ?? params?.value ?? params?.data;
              return (
                dataFormat({
                  value,
                  formatRule: firstBarConfig?.formatRule,
                }) || ''
              );
            },
          },
          emphasis: {
            disabled: true,
          },
          ...secondBarConfig?.seriesConfig,
        },
      ];

      // 表格
      const formattedTableData = tableData?.map((item: any) => ({
        region: getName(isEN, item?.regionNameEn, item?.regionNameCn),
        country: getName(isEN, item?.countryNameEn, item?.countryNameCn),
        os: getName(isEN, item?.osNameEn, item?.osNameCn),
        ...item,
        jumpUrl: tableCountryJumpLink
          ? dynamicString(tableCountryJumpLink, {
            countryCode: item?.countryCode || item?.dim_code,
            selectValue: effectiveMetricUrlMap?.[selectValue] || selectValue,
            gameCode: data?.gameCode,
            filter: data?.filterVal || data?.filter,
          })
          : undefined,
      }));

      return {
        barSeries,
        lineSeries,
        tableData,
        dauBarSeries,
        formattedTableData,
      };
    }, [chartData, isEN, tableCountryJumpLink, selectValue, data?.gameCode, data?.filterVal]);

  // 当数据变化时，重置排序数据为原始数据
  useEffect(() => {
    if (formattedTableData?.length > 0) {
      setSortedTableData(formattedTableData);
    } else {
      setSortedTableData([]);
    }
  }, [formattedTableData]);

  // 动态列配置：随 alignment + 下拉选项映射列（同一批 row 数据，不同 code / 表头）
  const currentColumns = useMemo(() => {
    const baseColumns = effectiveMetricsColumnMap
      ? (effectiveMetricsColumnMap[selectValue] || tableConfig?.columns)
      : tableConfig?.columns;
    return applyTableColumnHeaderSwitch(
      baseColumns,
      tableColumnHeaderSwitch,
      effectiveAlignmentValue,
    );
  }, [
    effectiveMetricsColumnMap,
    selectValue,
    tableConfig?.columns,
    tableColumnHeaderSwitch,
    effectiveAlignmentValue,
  ]);

  /**
   * 进度条宽度"按列范围归一化"：任意列只要配置
   * `column.features.cell.progressBarProps.basedOnColumnRange === true` 即可启用
   * （如"周环比（PP）"等绝对差值列，也适用于其它需要按列对比的数值列）：
   * - 正数以该列最大正数为分母；负数以该列最小负数（即绝对值最大的负数）为分母
   * - 颜色仍由数值正负决定（升绿降红）
   * 实现：
   * 1) 对每个标记列，计算列基准；为每行注入 `__col_range_pct_${code}` 字段
   * 2) 给列追加 `progressBarProps.widthKey`，让 PercentageCell 取用
   * 3) 同时把 cell.type 默认置为 'percentage'，确保走 PercentageCell（这类列 format 通常非 percent）
   */
  const { enhancedColumns, enhancedTableData } = useMemo(() => {
    const cols: IColumn[] = Array.isArray(currentColumns) ? currentColumns : [];
    const rows: TTableRow[] = Array.isArray(formattedTableData) ? formattedTableData : [];

    // 读取列上的进度条配置（features.cell 为开放结构，此处收窄到本逻辑关心的字段）
    const getRangeCell = (col: IColumn): IColumnRangeCell | undefined =>
      col?.features?.cell as IColumnRangeCell | undefined;
    const isColumnRangeBased = (col: IColumn): boolean =>
      getRangeCell(col)?.progressBarProps?.basedOnColumnRange === true;

    // 统一数值解析：非有限数返回 undefined
    const toFiniteNumber = (raw: unknown): number | undefined => {
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
      return Number.isFinite(num) ? num : undefined;
    };
    const widthFieldOf = (code: string): string => `__col_range_pct_${code}`;

    // 找出需要按列范围归一化的列
    const rangeCols = cols.filter(isColumnRangeBased);

    if (rangeCols.length === 0 || rows.length === 0) {
      return { enhancedColumns: cols, enhancedTableData: rows };
    }

    // 计算每个目标列的正/负基准
    const colBaseMap: Record<string, IColumnRangeBase> = {};
    rangeCols.forEach((col) => {
      const code = col?.code;
      if (!code) return;
      let posBase = 0;
      let negBase = 0;
      rows.forEach((row) => {
        const num = toFiniteNumber(row?.[code]);
        if (num === undefined) return;
        if (num > posBase) posBase = num;
        if (num < negBase) negBase = num;
      });
      colBaseMap[code] = { posBase, negBase };
    });

    // 给每行注入归一化后的宽度百分比字段
    const newRows: TTableRow[] = rows.map((row) => {
      const next: TTableRow = { ...row };
      rangeCols.forEach((col) => {
        const code = col?.code;
        if (!code) return;
        const num = toFiniteNumber(row?.[code]);
        if (num === undefined || num === 0) {
          next[widthFieldOf(code)] = 0;
          return;
        }
        const base = num > 0
          ? colBaseMap[code]?.posBase
          : Math.abs(colBaseMap[code]?.negBase || 0);
        next[widthFieldOf(code)] = base
          ? Math.min((Math.abs(num) / base) * 100, 100)
          : 0;
      });
      return next;
    });

    // 给目标列注入 widthKey（默认 cell.type='percentage' 走 PercentageCell 渲染）
    const newCols: IColumn[] = cols.map((col) => {
      if (!isColumnRangeBased(col)) {
        return col;
      }
      const code = col?.code || '';
      const cell = getRangeCell(col) || {};
      return {
        ...col,
        features: {
          ...col.features,
          cell: {
            type: 'percentage',
            ...cell,
            progressBarProps: {
              ...(cell.progressBarProps || {}),
              widthKey: widthFieldOf(code),
            },
          },
        },
      };
    });

    return { enhancedColumns: newCols, enhancedTableData: newRows };
  }, [currentColumns, formattedTableData]);

  // 业务专属的截图选项：撑开 ali-react-table 的滚动容器，并临时给表格加底边线
  const imageExportOptions = useMemo(() => ({
    expandSelectors: ['.db-alitable-wrapper', '.art-table-body'],
    hideSelectors: ['.dbd-metrics-card-wrap__title'],
    padding: { bottom: 10 },
    onBeforeCapture: async (root: HTMLElement) => {
      const restoreFns: Array<() => void> = [];
      const restoreAll = (): void => {
        restoreFns.forEach(fn => {
          try {
            fn();
          } catch (e) {
            console.warn('[ChangeAnalysisCountryRegionChart] imageExport restore failed:', e);
          }
        });
      };

      try {
        // 将表格容器撑宽到 imageExportTableWidth，各列按原始比例自动分配
        if (imageExportTableWidth) {
          const prevRootWidth = root.style.width;
          // eslint-disable-next-line no-param-reassign
          root.style.width = imageExportTableWidth;
          restoreFns.push(() => {
            // eslint-disable-next-line no-param-reassign
            root.style.width = prevRootWidth;
          });
        }

        const wrapper = root.querySelector<HTMLElement>('.db-alitable-wrapper');
        if (wrapper) {
          const prevBorderBottom = wrapper.style.borderBottom;
          wrapper.style.borderBottom = '';
          restoreFns.push(() => {
            wrapper.style.borderBottom = prevBorderBottom;
          });
        }

        // 将 svg use 引用的 iconfont symbol 内联，避免 html2canvas 截图时图标丢失
        restoreFns.push(inlineSvgUseElements(root));

        if (imageExportRowHeight) {
          injectCompactTableStyle(
            imageExportRowHeight,
            imageExportHeaderFontSize,
            imageExportContentFontSize,
          );
          restoreFns.push(() => {
            removeCompactTableStyle();
          });
          // 等行高 / padding CSS 生效后再测量 td 实际高度并居中色块
          await new Promise<void>(resolve => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
          });
          restoreFns.push(
            applyExportProgressBarInlineStyles(root, {
              rowHeight: imageExportRowHeight,
              progressBarHeight: imageExportProgressBarHeight,
            }),
          );
        }

        // 宽表 + 紧凑行高变更后再等一帧，避免 html2canvas 读取未稳定布局
        await new Promise<void>(resolve => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        await new Promise<void>(resolve => {
          setTimeout(resolve, 50);
        });

        return restoreAll;
      } catch (error) {
        restoreAll();
        throw error;
      }
    },
  }), [
    imageExportRowHeight,
    imageExportHeaderFontSize,
    imageExportContentFontSize,
    imageExportProgressBarHeight,
    imageExportTableWidth,
  ]);

  // 获取下载数据：表格视图或 tableOnly 模式时使用排序后的数据，否则使用原始格式化数据
  const downloadTableData = useMemo(() => {
    if ((activeType === 'sheet' || tableOnly) && sortedTableData.length > 0) {
      return sortedTableData;
    }
    return formattedTableData;
  }, [activeType, sortedTableData, formattedTableData, tableOnly]);

  // ===== AI 副标题内容 =====
  let subTitleContent;
  // 参数切换中 / 数据加载中 / AI 接口加载中，显示 loading
  if (isParamsSwitching || isAlignmentAiSwitching || loading || aiLoading) {
    subTitleContent = <Loading />;
  }
  // 有配置固定副标题时，使用配置内容
  else if (subTitle) {
    subTitleContent = getName(isEN, subTitle, subTitleCn);
  }
  // 临时关闭条件判断，方便后端调试
  // else if (!compareBarNodata && !barLineNodata) {
  //   subTitleContent = aiSummary || aiData;
  // }
  // 优先使用接口返回的缓存摘要（aiSummary），否则使用 AI 接口返回的数据（aiData）
  else {
    subTitleContent = inlineAiSummary || aiData;
  }

  // ===== 操作区（下载按钮等）是否显示 =====
  // tableOnly 模式：有表格数据时显示；图表模式：有图表数据时显示
  const showOperationArea = tableOnly
    ? tableData?.length > 0
    : !(barLineNodata || barLineNodata);
  /** 宽表数据就绪后再展示 Excel / 图片导出，避免 loading 或切换参数期间导出空表或半成品 DOM */
  const showDownloadActions =
    showOperationArea && !loading && !isParamsSwitching;

  /** 柱状/双柱区域高度：外层 Shell 开启且进入全屏时拉长 */
  const chartBlockHeight = useMemo(() => {
    if (!viewportFullscreen?.enabled || !viewportFullscreen?.isFullScreen) {
      return 375;
    }
    return Math.max(280, (viewportFullscreen.viewportHeight - 260) / 2);
  }, [
    viewportFullscreen?.enabled,
    viewportFullscreen?.isFullScreen,
    viewportFullscreen?.viewportHeight,
  ]);

  /**
   * 视口全屏时覆盖 tableHeight：见 FULLSCREEN_TABLE_CHROME_RESERVE。
   */
  const fullscreenTableHeight = useMemo(() => {
    if (!viewportFullscreen?.enabled || !viewportFullscreen?.isFullScreen) {
      return undefined;
    }
    return Math.max(
      240,
      viewportFullscreen.viewportHeight - FULLSCREEN_TABLE_CHROME_RESERVE,
    );
  }, [
    viewportFullscreen?.enabled,
    viewportFullscreen?.isFullScreen,
    viewportFullscreen?.viewportHeight,
  ]);

  /**
   * 与表格主体 tableHeight 一致，传入 CommonTable → Abnormal，避免加载中/失败/无数据时区域高度塌缩
   * （非简中重点国家表现等 tableOnly 场景）
   */
  const tableAbnormalMinHeight = useMemo(() => {
    if (fullscreenTableHeight !== undefined) {
      return fullscreenTableHeight;
    }
    const h = tableConfig?.tableConfig?.tableHeight;
    return typeof h === 'number' ? h : undefined;
  }, [fullscreenTableHeight, tableConfig?.tableConfig?.tableHeight]);

  // ===== 渲染内容区域（消除嵌套三元表达式） =====
  const renderContentArea = (): React.ReactNode => {
    // 纯表格模式（tableOnly）
    if (tableOnly) {
      return (
        <div ref={tableExportRef}>
          <CommonTable
            key={`table-${selectValue}-${segmentValue}`}
            ref={tableRef}
            data={commonTableDataForSort}
            className="dbd-change-analysis-country-region-chart-table"
            metricsCardWrapProps={{
              titleEn: tableConfig?.titleEn,
              titleCn: tableConfig?.titleCn,
            }}
            commonBaseTableProps={{
              isLoading: loading,
              hasError: errorMsg,
              dataSource: enhancedTableData,
              ...tableConfig,
              columns: enhancedColumns,
              ...(tableAbnormalMinHeight !== undefined
                ? { height: tableAbnormalMinHeight }
                : {}),
              tableConfig: {
                ...tableConfig?.tableConfig,
                ...(fullscreenTableHeight !== undefined
                  ? { tableHeight: fullscreenTableHeight }
                  : {}),
                onSortChange: handleTableSortChange,
              },
            }}
          />
        </div>
      );
    }

    // 图表视图
    if (activeType === 'column') {
      return (
        <>
          {/* 柱状折线图 */}
          <div className="dbd-change-analysis-country-region-chart-title">
            {getName(
              isEN,
              barLineChartConfig?.titleEn,
              barLineChartConfig?.titleCn,
            )}
          </div>
          <Abnormal
            isLoading={loading || isResetting}
            isError={errorMsg}
            style={{ height: chartBlockHeight }}
            isNoData={barLineNodata}
            component={
              <>
                <div className="dbd-change-analysis-country-region-chart-y-axis-name">
                  <div>
                    {getName(
                      isEN,
                      barMetricConfig?.enName,
                      barMetricConfig?.cnName,
                    )}
                  </div>
                  <div>
                    {getName(
                      isEN,
                      lineMetricConfig?.enName,
                      lineMetricConfig?.cnName,
                    )}
                  </div>
                </div>
                <BarChart
                  ref={chartRef}
                  xAxisData={xAxisData}
                  series={[...barSeries, ...lineSeries]}
                  height={chartBlockHeight}
                  colorArr={
                    barLineChartConfig?.colorArr || ['#69D19C', '#6E7794']
                  }
                  isShowLegend={true}
                  chartEventConfig={(option: any, chartInstance: any) => {
                    // 绑定点击事件
                    chartInstance.on('click', (params: any) => {
                      if (barLineChartConfig?.jumpLink) {
                        const clickedIndex = params.dataIndex;
                        const region = {
                          regionCode: countryCode?.[clickedIndex],
                        };
                        const url = dynamicString(
                          barLineChartConfig?.jumpLink,
                          { ...region, ...data },
                        );

                        window.open(url);
                      }
                    });
                    return option;
                  }}
                  formatterFunc={(params: any) => {
                    const tooltipData = params.map(
                      (item: {
                        value: number;
                        data: any;
                        seriesType: string;
                        seriesName: string;
                        color: string;
                      }) => {
                        const value =
                          item?.data?.value ?? item?.value ?? item?.data;
                        // 根据 series 类型应用不同的 formatRule
                        const isBarSeries = item.seriesType === 'bar';
                        const formattedValue = isBarSeries
                          ? dataFormat({
                            value,
                            formatRule: barMetricConfig?.formatRule,
                          })
                          : dataFormat({
                            value,
                            formatRule: lineMetricConfig?.formatRule,
                          });

                        return {
                          name: item.seriesName,
                          value: formattedValue,
                          color: item.color,
                        };
                      },
                    );

                    return getEchartTooltipString({
                      table: {
                        data: tooltipData,
                        columns: [
                          {
                            key: 'name',
                            header: params[0].axisValue,
                            showIcon: true,
                          },
                          {
                            key: 'value',
                            align: 'right',
                          },
                        ],
                      },
                    });
                  }}
                  setOtherConfigFunc={(option: any) => {
                    // 配置双Y轴
                    option.yAxis = [
                      {
                        type: 'value',
                        position: 'left',
                        axisLabel: {
                          showMaxLabel: false,
                          formatter: (value: number) =>
                            dataFormat({
                              value,
                              formatRule: barMetricConfig?.yAxisFormat,
                            }),
                        },
                        splitLine: {
                          show: true,
                          showMaxLine: false,
                          lineStyle: {
                            type: 'dashed',
                          },
                        },
                      },
                      {
                        type: 'value',
                        position: 'right',
                        axisLabel: {
                          formatter: (value: number) =>
                            dataFormat({
                              value,
                              formatRule: lineMetricConfig?.yAxisFormat,
                            }),
                        },
                        splitLine: {
                          show: false,
                        },
                      },
                    ];

                    // 配置X轴
                    option.xAxis = barLineChartConfig?.showCountryFlag
                      ? [
                        // 国旗icon
                        {
                          triggerEvent: true,
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          axisLine: { show: false },
                          axisTick: { show: false },
                          axisLabel: {
                            interval: 0, // 强制显示所有标签
                            formatter: (_value: number, index: number) =>
                              `{flag${index}|}`,
                            rich: countryCode?.reduce((acc: any, item: any, index: any) => {
                              const flagUrl = getCountryFlag(item);
                              acc[`flag${index}`] = {
                                backgroundColor: { image: flagUrl },
                                height: 18,
                                width: 24,
                                align: 'center',
                              };
                              return acc;
                            }, {}),
                          },
                        },
                        // 第一层：文字（倾斜）
                        {
                          triggerEvent: true,
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          axisLine: { show: false },
                          axisTick: { show: false },
                          offset: 35,
                          axisLabel: {
                            interval: 0, // 强制显示所有标签
                            rotate: -30, // 文字倾斜
                            fontSize: 12,
                            color: '#838697',
                            align: 'left',
                            formatter: (value: string) => value,
                          },
                        },
                      ]
                      : [
                        {
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          offset: 10,
                          axisLine: { show: false },
                          axisTick: { show: false },
                          axisLabel: {
                            rotate: -30, // 文字倾斜
                            fontSize: 12,
                            color: '#838697',
                            formatter: (value: string) => value,
                          },
                        },
                      ];
                    option.legend = {
                      ...option.legend,
                      padding: [0, 0, 10, 0],
                      data: option.series.map((s: any) => {
                        // 检查当前 series 对应的配置是否有 icon
                        const hasCustomIcon =
                            (s.name === getName(isEN, barMetricConfig?.enName, barMetricConfig?.cnName) && barMetricConfig?.icon) ||
                            (s.name === getName(isEN, lineMetricConfig?.enName, lineMetricConfig?.cnName) && lineMetricConfig?.icon);

                        return {
                          name: s.name,
                          icon: hasCustomIcon ? 'none' : (s.legendIcon || 'circle'),
                        };
                      }),
                      formatter: (name: string) => {
                        const series = option.series.find((s: any) => s.name === name);

                        // 判断是 bar 还是 line series，并检查对应的 icon 配置
                        let customIcon = null;
                        if (series.name === getName(isEN, barMetricConfig?.enName, barMetricConfig?.cnName)) {
                          customIcon = barMetricConfig?.icon;
                        } else if (series.name === getName(isEN, lineMetricConfig?.enName, lineMetricConfig?.cnName)) {
                          customIcon = lineMetricConfig?.icon;
                        }

                        // 只有配置了 icon 的才使用 rich text
                        if (customIcon) {
                          return `{specialIcon|}{gap|} ${name}`;
                        }
                        return name;
                      },
                      textStyle: {
                        rich: {
                          specialIcon: {
                            width: 20,
                            height: 8,
                            backgroundColor: {
                              image: (iconMap as any)[barMetricConfig?.icon] || (iconMap as any)[lineMetricConfig?.icon]
                            }
                          },
                        }
                      }
                    };
                    return option;
                  }}
                />
              </>
            }
          />

          {/* 双柱状图 */}
          <div style={{ marginTop: '20px' }}>
            <div className="dbd-change-analysis-country-region-chart-title">
              {getName(
                isEN,
                doubleBarChartConfig?.titleEn,
                doubleBarChartConfig?.titleCn,
              )}
            </div>
            <Abnormal
              isLoading={loading || isResetting}
              isError={errorMsg}
              isNoData={compareBarNodata}
              style={{ height: chartBlockHeight }}
              component={
                <BarChart
                  xAxisData={xAxisData}
                  series={dauBarSeries}
                  height={chartBlockHeight}
                  colorArr={
                    doubleBarChartConfig?.colorArr || ['#5086F3', '#69D19C']
                  }
                  isShowLegend={true}
                  chartEventConfig={(option: any, chartInstance: any) => {
                    // 绑定点击事件
                    chartInstance.on('click', (params: any) => {
                      if (barLineChartConfig?.jumpLink) {
                        const clickedIndex = params.dataIndex;
                        const region = {
                          regionCode: countryCode?.[clickedIndex],
                        };
                        const url = dynamicString(
                          barLineChartConfig?.jumpLink,
                          { ...region, ...data },
                        );

                        window.open(url);
                      }
                    });
                    return option;
                  }}
                  formatterFunc={(params: any) => {
                    const tooltipData = params.map((item: any) => ({
                      name: item.seriesName,
                      value: dataFormat({
                        value: item?.value,
                        formatRule: secondBarConfig?.formatRule,
                      }),
                      color: item.color,
                    }));

                    return getEchartTooltipString({
                      table: {
                        data: tooltipData,
                        columns: [
                          {
                            key: 'name',
                            header: params[0].axisValue,
                            showIcon: true,
                          },
                          {
                            key: 'value',
                            align: 'right',
                          },
                        ],
                      },
                    });
                  }}
                  setOtherConfigFunc={(option: any) => {
                    // 配置Y轴
                    option.yAxis = {
                      type: 'value',
                      axisLabel: {
                        formatter: (value: number) =>
                          dataFormat({
                            value,
                            formatRule: firstBarConfig?.yAxisFormat,
                          }),
                      },
                      splitLine: {
                        show: true,
                        lineStyle: {
                          type: 'dashed',
                        },
                      },
                    };

                    // 配置X轴
                    option.xAxis = doubleBarChartConfig?.showCountryFlag
                      ? [
                        {
                          triggerEvent: true,
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          axisLine: { show: false },
                          axisTick: { show: false },
                          axisLabel: {
                            interval: 0, // 强制显示所有标签
                            margin: 10,
                            formatter: (_value: number, index: number) =>
                              `{flag${index}|}`,
                            rich: countryCode?.reduce((acc: any, item: any, index: any) => {
                              const flagUrl = getCountryFlag(item);
                              acc[`flag${index}`] = {
                                backgroundColor: { image: flagUrl },
                                height: 18,
                                width: 24,
                                align: 'center',
                              };
                              return acc;
                            }, {}),
                          },
                        },
                        // 第一层：文字（倾斜）
                        {
                          triggerEvent: true,
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          axisLine: { show: false },
                          axisTick: { show: false },
                          offset: 35,
                          axisLabel: {
                            interval: 0, // 强制显示所有标签
                            rotate: -30, // 文字倾斜
                            fontSize: 12,
                            color: '#838697',
                            align: 'left',
                            formatter: (value: string) => value,
                          },
                        },
                      ]
                      : [
                        {
                          type: 'category',
                          data: xAxisData,
                          position: 'bottom',
                          offset: 10,
                          axisLine: { show: false },
                          axisTick: { show: false },
                          axisLabel: {
                            rotate: -30, // 文字倾斜
                            fontSize: 12,
                            color: '#838697',
                            formatter: (value: string) => value,
                          },
                        },
                      ];

                    option.grid={
                      ...option.grid,
                      right: "50",
                    };

                    return option;
                  }}
                />
              }
            />
          </div>
        </>
      );
    }

    // 表格视图（原有 sheet 模式）
    return (
      <div ref={tableExportRef}>
        <CommonTable
          key={`table-${selectValue}-${segmentValue}`}
          ref={tableRef}
          data={commonTableDataForSort}
          className="dbd-change-analysis-country-region-chart-table"
          metricsCardWrapProps={{
            titleEn: tableConfig?.titleEn,
            titleCn: tableConfig?.titleCn,
          }}
          commonBaseTableProps={{
            isLoading: loading,
            hasError: errorMsg,
            dataSource: enhancedTableData,
            ...tableConfig,
            columns: enhancedColumns,
            ...(tableAbnormalMinHeight !== undefined
              ? { height: tableAbnormalMinHeight }
              : {}),
            tableConfig: {
              ...tableConfig?.tableConfig,
              ...(fullscreenTableHeight !== undefined
                ? { tableHeight: fullscreenTableHeight }
                : {}),
              onSortChange: handleTableSortChange,
            },
          }}
        />
      </div>
    );
  };

  const mainCard = (
    <div
      ref={cardRef}
      className="dbd-change-analysis-country-region-chart-wrap"
    >
      <MetricsCardWrap
        className={classNames(
          'dbd-change-analysis-country-region-chart',
          className,
        )}
        style={style}
        cardHeader={
          <><div className="dbd-change-analysis-country-region-chart-card-header">
            {/* 左侧区域 */}
            <div className="dbd-change-analysis-country-region-chart-left">
              {tabOption && (
                <Segment
                  options={tabOptions}
                  value={(segmentValue ?? tabOption?.defaultValue) as never}
                  onChange={(value: string | number) => {
                    if (String(value) === String(segmentValue)) {
                      return;
                    }
                    purgeClientTableSort();
                    setSegmentValue(value);
                    // 立即标记参数切换中，AI 文案区域立刻显示 loading
                    setIsParamsSwitching(true);
                  } } />
              )}
              {(effectiveSelectConfig ||
                (!isAlignmentSwitchHidden && alignmentOptions.length > 0)) && (
                <div className="dbd-change-analysis-country-region-chart-metric-row">
                  {effectiveSelectConfig && (
                    <SingleSelect
                      className="dbd-change-analysis-country-region-chart-select"
                      options={selectOptions}
                      value={selectValue}
                      tooltip={effectiveSelectConfig?.tooltip}
                      name={getName(
                        isEN,
                        effectiveSelectConfig?.titleEn,
                        effectiveSelectConfig?.titleCn
                      )}
                      getPopupContainer={
                        viewportFullscreen?.enabled &&
                        viewportFullscreen?.isFullScreen
                          ? () => document.body
                          : undefined
                      }
                      onChange={(value: string) => {
                        if (value === selectValue) {
                          return;
                        }
                        purgeClientTableSort();
                        setSelectValue(value);
                        setTopInfo(selectOptions?.find((item: { value: string; }) => item.value === value)?.topInfo);
                        setIsParamsSwitching(true);
                      } }
                      {...effectiveSelectConfig} />
                  )}
                  {!isAlignmentSwitchHidden && alignmentOptions.length > 0 && (
                    <Radio.Group
                      className="dbd-change-analysis-country-region-chart-alignment"
                      value={alignmentValue}
                      onChange={(val: string) => {
                        if (val === alignmentValue) {
                          return;
                        }
                        setAlignmentValue(val);
                        if (enableAlignmentAiDataKey) {
                          const nextDataKeyField = getAlignmentAiDataKeyField(
                            val,
                            alignmentDataKeyMap,
                          );
                          const nextAiDataKey = req?.data_keys?.[nextDataKeyField];
                          const nextAiSummary = nextAiDataKey
                            ? req?.ai_analysis?.[nextDataKeyField]
                            : req?.data_analysis;
                          setIsAlignmentAiSwitching(
                            Boolean(nextAiDataKey && nextAiDataKey !== currentAiDataKey && !nextAiSummary),
                          );
                        }
                      }}
                    >
                      {alignmentOptions.map((opt) => (
                        <Radio key={opt.value} name={opt.value}>
                          {opt.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  )}
                </div>
              )}

            </div>

            {/* 右侧操作区 */}
            {showOperationArea &&
            <div
              className="dbd-change-analysis-country-region-chart-operation"
              data-export-hidden="true"
            >
              {/* tableOnly 模式下隐藏图表/表格切换按钮，只保留下载 */}
              {!tableOnly && (
                <div className="dbd-operation-btn dbd-operation-hover-show">
                  {operationList.map((item) => (
                    <TipContext
                      key={item.key}
                      tipText={item.tipText}
                      type={item.type}
                      onClick={() => handleActiveTypeChange(item.key)}
                      className={classNames({
                        'dbd-change-analysis-country-region-chart-action': activeType === item.key,
                      })} />
                  ))}
                </div>
              )}

              {showDownloadActions && enableImageDownload && (
                <ImageDownloadButton
                  className="dbd-operation-btn"
                  targetRef={tableExportRef}
                  filename={downLoadFileTitle}
                  exportOptions={imageExportOptions}
                />
              )}
              {showDownloadActions && (
                <DownloadButton
                  className="dbd-operation-btn"
                  tableColumns={enhancedColumns}
                  tableData={downloadTableData}
                  tableTitle={downLoadFileTitle}
                />
              )}
              {viewportFullscreen?.enabled ? (
                <ViewportFullscreenTip
                  className="dbd-operation-btn dbd-viewport-fullscreen-tip"
                  isFullScreen={viewportFullscreen.isFullScreen}
                  requestFullScreen={viewportFullscreen.requestFullScreen}
                  exitFullScreen={viewportFullscreen.exitFullScreen}
                />
              ) : null}
            </div>
            }
          </div>
          <div className="dbd-change-analysis-country-region-chart-subtitle">
            {subTitleContent}
          </div></>
        }
        {...restProps}
      >
        {renderContentArea()}
      </MetricsCardWrap>
    </div>
  );

  return mainCard;
};

export default ChangeAnalysisCountryRegionChart;
